import type { CompiledStyles, InlineStyles, StyleXArray } from "@stylexjs/stylex";
import * as stylex from "@stylexjs/stylex";

/** Style input accepted by `stylex.props`: a single style, a nested array of styles, or a dynamic style tuple. */
export type SxStyle = StyleXArray<null | undefined | CompiledStyles | boolean | Readonly<[CompiledStyles, InlineStyles]>>;

/**
 * Merges StyleX styles with an external `className` and `style`.
 *
 * Zendesk Garden clones the elements it is handed and injects its own `className` on them, so a component that
 * renders one of those elements has to combine both sides instead of letting either one replace the other. Local
 * StyleX class names come first; external inline styles override conflicting StyleX inline values.
 *
 * @param styles The component's own StyleX styles.
 * @param className The external class name to append, if any.
 * @param style The external inline style to merge, if any.
 * @returns The className and style props to spread onto the rendered element.
 */
export function mergedSx(styles: SxStyle, className: string | undefined, style?: React.CSSProperties): {
  className?: string;
  style?: React.CSSProperties;
} {
  const props = stylex.props(styles);
  return {
    ...props,
    className: className ? props.className ? `${props.className} ${className}` : className : props.className,
    style: style ? {
      ...props.style,
      ...style
    } : props.style
  } as {
    className?: string;
    style?: React.CSSProperties;
  };
}